import { useEffect, useState , useCallback} from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Send, CheckCircle2, XCircle, Loader2, Clock, ShieldCheck, AlertTriangle, CheckSquare, Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";

const STATUS_TABS = [
  { value: "pending", label: "Pending", icon: Clock },
  { value: "approved", label: "Approved", icon: CheckCircle2 },
  { value: "auto_approved", label: "Auto-approved", icon: ShieldCheck },
  { value: "rejected", label: "Rejected", icon: XCircle },
  { value: "all", label: "All", icon: AlertTriangle },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminClaimsTab() {
  const [status, setStatus] = useState("pending");
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [sending, setSending] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState(null); // { claim, action: "approve"|"reject" }
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

    const fetchClaims = useCallback(async (s = status) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await axios.get(`${API}/admin/claims`, {
        params: { status: s, limit: 200 },
        withCredentials: true,
      });
      setClaims(res.data?.claims || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load claims");
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchClaims(status);
  }, [fetchClaims, status]);

  const pendingClaims = claims.filter((c) => c.status === "pending");
  const allSelectedOnPage =
    pendingClaims.length > 0 && pendingClaims.every((c) => selected.has(c.claim_id));

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(allSelectedOnPage ? new Set() : new Set(pendingClaims.map((c) => c.claim_id)));
  };

  const bulkApprove = async ({ useSelection }) => {
    if (useSelection && selected.size === 0) {
      toast.error("Nothing selected");
      return;
    }
    if (
      !window.confirm(
        useSelection
          ? `Approve ${selected.size} selected claim${selected.size === 1 ? "" : "s"}? Each transfers ownership + sends the claimant an email.`
          : `Approve ALL pending claims (${pendingClaims.length})? Each transfers ownership + sends the claimant an email. This can't be undone.`
      )
    )
      return;

    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/claims/bulk-approve`,
        useSelection
          ? { claim_ids: Array.from(selected) }
          : { approve_all_pending: true },
        { withCredentials: true }
      );
      const { approved_count, skipped_count } = res.data || {};
      toast.success(
        `Approved ${approved_count}${skipped_count ? ` · Skipped ${skipped_count}` : ""}`
      );
      setSelected(new Set());
      fetchClaims(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const sendInvites = async () => {
    if (sending) return;
    if (!confirm("Send claim-invitation emails to every unclaimed business now?")) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/claims/send-invites`,
        {},
        { withCredentials: true }
      );
      const { sent, skipped, total_unclaimed } = res.data || {};
      toast.success(
        `Sent ${sent} invite${sent === 1 ? "" : "s"}. Skipped ${skipped} (no email). Total unclaimed: ${total_unclaimed}.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invites");
    } finally {
      setSending(false);
    }
  };

  const sendTestInvite = async () => {
    if (sending) return;
    const businessId = window.prompt(
      "Send a TEST claim invite.\n\nBusiness ID (find it in /admin/businesses, e.g. biz_218e52940eb4):"
    );
    if (!businessId) return;
    const emailOverride = window.prompt(
      "Route the invite email to which address? Leave blank to use the business's own email.\n\nRecommended: your admin email so you can preview the template."
    );
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/claims/send-invite-one`,
        {
          business_id: businessId.trim(),
          email_override: (emailOverride || "").trim() || null,
        },
        { withCredentials: true }
      );
      toast.success(
        `Test invite sent → ${res.data.sent_to} (business: ${res.data.business_name}).`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test invite");
    } finally {
      setSending(false);
    }
  };

  const submitDecision = async () => {
    if (!decisionTarget) return;
    const { claim, action } = decisionTarget;
    setBusyId(claim.claim_id);
    try {
      await axios.post(
        `${API}/admin/claims/${claim.claim_id}/${action}`,
        { reason: reason || null },
        { withCredentials: true }
      );
      toast.success(
        action === "approve"
          ? `Approved — ${claim.business_name} now belongs to ${claim.claimant_email}`
          : `Claim rejected for ${claim.business_name}`
      );
      setDecisionTarget(null);
      setReason("");
      fetchClaims(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update claim");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-claims-tab">
      {/* Header / actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading text-tbc-navy">Ownership claims</h2>
          <p className="text-sm text-gray-500">
            Track owners claiming seeded businesses. Auto-approved when the Google
            email matches the business contact email.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={sendTestInvite}
            disabled={sending}
            className="border-tbc-navy text-tbc-navy hover:bg-tbc-navy/5"
            data-testid="send-test-invite-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Send test invite
          </Button>
          <Button
            onClick={sendInvites}
            disabled={sending}
            className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
            data-testid="send-claim-invites-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send claim invites to unclaimed
          </Button>
        </div>
      </div>

      {/* Bulk-approve toolbar — only meaningful on the 'pending' tab. */}
      {status === "pending" && pendingClaims.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-tbc-navy/10 bg-tbc-cream/30"
          data-testid="claims-bulk-toolbar"
        >
          <button
            onClick={toggleSelectAll}
            className="text-xs text-tbc-navy hover:underline flex items-center gap-1.5"
            data-testid="claims-select-all-toggle"
          >
            {allSelectedOnPage ? (
              <CheckSquare className="w-3.5 h-3.5" />
            ) : (
              <Square className="w-3.5 h-3.5" />
            )}
            {allSelectedOnPage
              ? "Clear selection"
              : `Select all pending (${pendingClaims.length})`}
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {selected.size > 0 && (
              <Button
                size="sm"
                onClick={() => bulkApprove({ useSelection: true })}
                disabled={bulkBusy}
                className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
                data-testid="claims-bulk-approve-selected"
              >
                {bulkBusy ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                )}
                Approve {selected.size} selected
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => bulkApprove({ useSelection: false })}
              disabled={bulkBusy}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="claims-bulk-approve-all"
            >
              {bulkBusy ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              )}
              Approve all pending ({pendingClaims.length})
            </Button>
          </div>
        </div>
      )}

      {/* Status tabs */}
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="flex flex-wrap">
          {STATUS_TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="flex items-center gap-2"
              data-testid={`claims-tab-${t.value}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {STATUS_TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-4">
            {loading ? (
              <div className="text-center py-10 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                Loading…
              </div>
            ) : claims.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="claims-empty">
                <Mail className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                No {t.label.toLowerCase()} claims.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                    <tr>
                      {status === "pending" && <th className="px-3 py-3 w-8" />}
                      <th className="px-4 py-3 text-left">Business</th>
                      <th className="px-4 py-3 text-left">Claimant</th>
                      <th className="px-4 py-3 text-left">Expected email</th>
                      <th className="px-4 py-3 text-left">Submitted</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {claims.map((c) => (
                      <tr key={c.claim_id} data-testid={`claim-row-${c.claim_id}`}>
                        {status === "pending" && (
                          <td className="px-3 py-3">
                            {c.status === "pending" && (
                              <button
                                onClick={() => toggleSelected(c.claim_id)}
                                aria-label={selected.has(c.claim_id) ? "Deselect" : "Select"}
                                data-testid={`select-claim-${c.claim_id}`}
                              >
                                {selected.has(c.claim_id) ? (
                                  <CheckSquare className="w-4 h-4 text-tbc-navy" />
                                ) : (
                                  <Square className="w-4 h-4 text-gray-300 hover:text-gray-500" />
                                )}
                              </button>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 font-medium text-tbc-navy">
                          {c.business_name || c.business_id}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{c.claimant_name || "—"}</div>
                          <div className="text-xs text-gray-500">{c.claimant_email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {c.expected_email || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(c.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              c.status === "approved" || c.status === "auto_approved"
                                ? "bg-green-100 text-green-700"
                                : c.status === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {c.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {c.status === "pending" ? (
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setDecisionTarget({ claim: c, action: "reject" });
                                  setReason("");
                                }}
                                disabled={busyId === c.claim_id}
                                data-testid={`reject-claim-${c.claim_id}`}
                              >
                                Reject
                              </Button>
                              <Button
                                size="sm"
                                className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                                onClick={() => {
                                  setDecisionTarget({ claim: c, action: "approve" });
                                  setReason("");
                                }}
                                disabled={busyId === c.claim_id}
                                data-testid={`approve-claim-${c.claim_id}`}
                              >
                                Approve
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {c.reason || "—"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Decision modal */}
      <Dialog
        open={!!decisionTarget}
        onOpenChange={(open) => !open && setDecisionTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionTarget?.action === "approve" ? "Approve claim" : "Reject claim"}
            </DialogTitle>
          </DialogHeader>
          {decisionTarget && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <strong>{decisionTarget.claim.business_name}</strong> →{" "}
                {decisionTarget.claim.claimant_email}
              </div>
              {decisionTarget.action === "approve" && (
                <div className="text-xs text-yellow-700 bg-yellow-50 rounded-md p-3 border border-yellow-200">
                  Approving transfers ownership of this business to the claimant. They&apos;ll
                  be able to edit it, add products, and respond to reviews.
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500">
                  {decisionTarget.action === "approve"
                    ? "Optional note to claimant"
                    : "Reason (sent in rejection email)"}
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    decisionTarget.action === "approve"
                      ? "Welcome aboard!"
                      : "We couldn't verify ownership from the email provided."
                  }
                  rows={3}
                  data-testid="claim-decision-reason"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setDecisionTarget(null)}
                  data-testid="claim-decision-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitDecision}
                  className={
                    decisionTarget.action === "approve"
                      ? "bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  }
                  disabled={busyId === decisionTarget.claim.claim_id}
                  data-testid="claim-decision-submit"
                >
                  {busyId === decisionTarget.claim.claim_id && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {decisionTarget.action === "approve" ? "Approve & transfer" : "Reject claim"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
